/**
 * Current value, colour, source and age of every data item: the store's
 * readings override the element JSON. One memoised lookup object per
 * readings snapshot so the tree and the dialogs agree.
 */
import { useMemo } from 'react';
import { readingKey, useTwin } from '../../store';
import type { DataItem, Reading, ReadingSource, StatusColour, Subsystem } from '../../types';
import { statusColourOf } from '../lib/values';

export interface ItemView {
  value: string;
  numeric: number | null;
  colour: StatusColour | null;
  source: ReadingSource;
  /** Reading time when a reading exists. */
  timeMs: number | null;
  freshnessMs: number | undefined;
  hasStoreHistory: boolean;
}

export interface ItemLookup {
  view: (subsystemId: string, item: DataItem) => ItemView;
  colourOf: (subsystemId: string, dataId: string) => StatusColour | null;
  valueOf: (subsystemId: string, dataId: string) => string;
  reading: (subsystemId: string, dataId: string) => Reading | undefined;
}

function indexItems(subsystems: Subsystem[]): Map<string, DataItem> {
  const map = new Map<string, DataItem>();
  const visit = (s: Subsystem) => {
    for (const d of s.data ?? []) map.set(readingKey(s.id, d.id), d);
    for (const c of s.childs ?? []) visit(c);
  };
  for (const s of subsystems) visit(s);
  return map;
}

export function useItemLookup(): ItemLookup {
  const element = useTwin((s) => s.element);
  const readings = useTwin((s) => s.readings);
  const history = useTwin((s) => s.history);
  return useMemo(() => {
    const items = element ? indexItems(element.subsystems) : new Map<string, DataItem>();
    const view = (subsystemId: string, item: DataItem): ItemView => {
      const key = readingKey(subsystemId, item.id);
      const r = readings[key];
      if (r) {
        return { value: r.value, numeric: r.numericValue, colour: r.status, source: r.source, timeMs: r.timeMs, freshnessMs: r.freshnessMs, hasStoreHistory: (history[key]?.length ?? 0) > 0 };
      }
      return { value: item.value, numeric: null, colour: statusColourOf(item.status), source: 'json', timeMs: null, freshnessMs: undefined, hasStoreHistory: (history[key]?.length ?? 0) > 0 };
    };
    return {
      view,
      colourOf: (subsystemId, dataId) => {
        const r = readings[readingKey(subsystemId, dataId)];
        if (r) return r.status;
        const item = items.get(readingKey(subsystemId, dataId));
        return item ? statusColourOf(item.status) : null;
      },
      valueOf: (subsystemId, dataId) => {
        const r = readings[readingKey(subsystemId, dataId)];
        if (r) return r.value;
        return items.get(readingKey(subsystemId, dataId))?.value ?? '';
      },
      reading: (subsystemId, dataId) => readings[readingKey(subsystemId, dataId)],
    };
  }, [element, readings, history]);
}

/** Subsystem colour: the derived status when present, else the worst of its own items and children. */
export function useSubsystemColour(): (s: Subsystem) => StatusColour | null {
  const statuses = useTwin((s) => s.statuses);
  const lookup = useItemLookup();
  return useMemo(() => {
    const colourOf = (s: Subsystem): StatusColour | null => {
      const derived = statuses[s.id];
      if (derived) return derived.colour;
      let worst: StatusColour | null = null;
      const consider = (c: StatusColour | null) => {
        if (c === 'red') worst = 'red';
        else if (c === 'yellow' && worst !== 'red') worst = 'yellow';
        else if (c === 'green' && worst === null) worst = 'green';
      };
      for (const d of s.data ?? []) consider(lookup.colourOf(s.id, d.id));
      for (const c of s.childs ?? []) consider(colourOf(c));
      return worst;
    };
    return colourOf;
  }, [statuses, lookup]);
}
